import { Button } from '@design-system/button';
import { ErrorMessage } from '@design-system/error-message';
import { FieldToShow, InputType, isErrorResult } from '@phx-husky/auth-service';
import { Field, FieldType as FieldTypeEnum } from '@phx-husky/field';
import { useForm } from '@phx-husky/use-form';
import { useProfile } from '@phx-husky/use-profile';
import classNames from 'classnames';
import React, { useMemo, useState } from 'react';

import { FormProps } from '../models';
import * as styles from './form.module.scss';

export const Form = <T,>({
  fields,
  onClose,
  onSubmit,
  cancelButtonLabel,
  submitButtonLabel,
  updateFieldsOnSubmit,
  onSuccess,
  closeButtonVisible,
  buttonsOrientation = 'right',
  className,
}: FormProps<T>) => {
  const { validate } = useProfile();
  const [errorMessage, setErrorMessage] = useState('');
  const [pending, setPending] = useState(false);

  const close = (shouldUpdateProfile = false) => {
    onClose?.({ shouldUpdateProfile });
  };

  const submitForm = async (data: T) => {
    setPending(true);
    setErrorMessage('');

    const form = Object.keys(fields).reduce((result, key) => {
      const value = data[key]?.value;

      if (value !== undefined) {
        return { ...result, [key]: value };
      }

      return { ...result, [key]: data[key] };
    }, {});

    const result = await onSubmit(form as T);

    if (isErrorResult(result)) {
      setErrorMessage(result.message || 'defaultErrorMessage');
    } else {
      onSuccess?.();
      close(updateFieldsOnSubmit);
    }
    setPending(false);
  };

  const fieldStatesInit = useMemo(
    () =>
      Object.keys(fields).reduce(
        (prev, curr) => ({
          ...prev,
          [curr]: fields[curr].defaultValue || '',
        }),
        {}
      ),
    [fields]
  );

  const { values, errors, handleChange, handleSubmit } = useForm({
    initialValues: fieldStatesInit,
    callback: submitForm,
    validate,
    validateOnChange: true,
  });

  const prepareValue = (field: FieldToShow, fieldValue: any) => {
    if (field.type !== InputType.select || !field.options) {
      return fieldValue;
    }

    return field.options.find((option) => option.value === fieldValue);
  };

  return (
    <div
      className={classNames(
        styles.formWrapper,
        'auth-profile-edit-nested',
        className
      )}
    >
      <form
        onSubmit={handleSubmit}
        className={classNames(styles.form, 'auth-profile-edit-nested__form')}
      >
        {Object.entries(fields)
          .filter(([, { showInEdit }]) => showInEdit)
          .map(([name, { label, type, options }]) => (
            <Field
              key={name}
              label={label as string}
              errorMessage={errors.validation?.[name].message}
              name={name}
              type={FieldTypeEnum[type]}
              value={prepareValue(fields[name], values[name])}
              onChange={handleChange}
              defaultValue={prepareValue(fields[name], values[name])}
              options={options}
              placeholder={label as string}
              className={classNames(
                styles.formItem,
                'auth-profile-edit-form__form-item'
              )}
            />
          ))}
        {errorMessage && (
          <ErrorMessage
            message={errorMessage}
            className="field__error-message"
          />
        )}
        <div
          className={classNames(
            buttonsOrientation === 'right'
              ? styles.buttonWrapperRight
              : styles.buttonWrapperLeft,
            styles.buttonWrapper,
            'auth-profile-edit-nested__button-wrapper'
          )}
        >
          {closeButtonVisible && (
            <Button
              variant="secondary"
              className={styles.cancelButton}
              onClick={() => close()}
            >
              {cancelButtonLabel || ''}
            </Button>
          )}
          <Button
            variant="primary"
            className={styles.submitButton}
            type="submit"
            isLoading={pending}
            disabled={pending}
          >
            {submitButtonLabel}
          </Button>
        </div>
      </form>
    </div>
  );
};
